import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACTION_TONE = { CREATE: "text-emerald-600", UPDATE: "text-blue-600", DELETE: "text-red-600", LOGIN: "text-slate-500", OVERRIDE: "text-amber-600" };

// Reusable audit viewer. extraQuery e.g. `&organisation_id=x` for tenant scope handled server-side.
export function AuditViewer({ query = "" }) {
  const [rows, setRows] = useState(null);
  const [severity, setSeverity] = useState("all");
  const [detail, setDetail] = useState(null);

  const load = () => {
    setRows(null);
    const sev = severity !== "all" ? `&severity=${severity}` : "";
    api.get(`/audit?limit=300${query}${sev}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(load, [severity, query]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="audit-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
            <SelectItem value="WARNING">Warning</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {rows === null ? <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div> : (
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Description</TableHead><TableHead>Severity</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((a, i) => (
                <TableRow key={i} className="hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                  <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-700">{a.user_name}</TableCell>
                  <TableCell><span className={`font-mono text-xs font-semibold ${ACTION_TONE[a.action] || "text-slate-600"}`}>{a.action}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{a.entity_type || "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-md truncate">{a.description}</TableCell>
                  <TableCell><StatusBadge status={a.severity} label={a.severity} /></TableCell>
                  <TableCell><Button variant="ghost" size="icon" onClick={() => setDetail(a)} data-testid={`audit-view-${i}`}><Eye className="h-4 w-4 text-slate-400" /></Button></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No audit records.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent data-testid="audit-detail-dialog">
          <DialogHeader><DialogTitle>Audit Record</DialogTitle></DialogHeader>
          {detail && <pre className="text-xs bg-slate-900 text-slate-100 rounded-md p-4 overflow-x-auto thin-scroll font-mono">{JSON.stringify(detail, null, 2)}</pre>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
